import useApplicationState, { selectModalSlice } from '@lib/state';
import {
    type AuthModalState,
    type CartModalState,
    type EditorModalState,
    type ModalComponent,
    type ModalComponentState,
    isEditorModalState
} from '@lib/state/Modal';

export {
    type ModalComponent,
    type ModalComponentState,
    type EditorModalState,
    type AuthModalState,
    type CartModalState as CheckoutModalState,
    isEditorModalState
};

interface UseModalProperties<C extends ModalComponent> {
    open: boolean;
    /**
     * The internal state of the modal, specific to the
     * component. e.g. if `auth` was passed to `useModal()` this would
     * be one of `login`, `createAccount`, or `forgotPassword`
     */
    state?: ModalComponentState<C>;
    /**
     * Opens the modal optionally initializing it to a state
     */
    onOpen: (state?: ModalComponentState<C>) => void;
    onClose: () => void;
    /**
     * Changes modal state
     */
    onChangeState: (state: ModalComponentState<C>) => void;
}

const useModal = <C extends ModalComponent>(forComponent: C): UseModalProperties<C> => {
    const { component, open, state, openModal, closeModal, changeState } =
        useApplicationState(selectModalSlice);

    const onOpen = (state?: ModalComponentState<C>) => {
        if (state) {
            changeState(state);
        }
        openModal(forComponent);
    };

    const onClose = () => {
        closeModal();
    };

    const onChangeState = (state: ModalComponentState<C>) => {
        changeState(state);
    };

    return {
        open: open && component === forComponent,
        state: state as ModalComponentState<C> | undefined,
        onOpen,
        onClose,
        onChangeState
    };
};

export default useModal;
